import { LayoutGrid, List, SlidersHorizontal, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

export type CatalogueSort =
  | "popular"
  | "relevance"
  | "updated"
  | "az"
  | "za";

export type CatalogueView = "grid" | "list";

export type FacetKey = "theme" | "format" | "region";

export const CATALOGUE_SORTS: { value: CatalogueSort; label: string }[] = [
  { value: "popular", label: "Most popular" },
  { value: "relevance", label: "Relevance" },
  { value: "updated", label: "Recently updated" },
  { value: "az", label: "Title A-Z" },
  { value: "za", label: "Title Z-A" },
];

export type FacetSelection = Record<FacetKey, Set<string>>;

interface CatalogueControlsProps {
  query: string;
  onQueryChange: (value: string) => void;
  sort: CatalogueSort;
  onSortChange: (value: CatalogueSort) => void;
  view: CatalogueView;
  onViewChange: (value: CatalogueView) => void;
  themeOptions: readonly string[];
  formatOptions: string[];
  regionOptions: string[];
  /** Format/region come from the datasets API, so they arrive after load. */
  facetsPending: boolean;
  selected: FacetSelection;
  onToggle: (facet: FacetKey, value: string) => void;
  onClearAll: () => void;
}

const FACET_LABELS: Record<FacetKey, string> = {
  theme: "Theme",
  format: "Format",
  region: "Region",
};

export const CatalogueControls = ({
  query,
  onQueryChange,
  sort,
  onSortChange,
  view,
  onViewChange,
  themeOptions,
  formatOptions,
  regionOptions,
  facetsPending,
  selected,
  onToggle,
  onClearAll,
}: CatalogueControlsProps) => {
  const activeCount =
    selected.theme.size + selected.format.size + selected.region.size;

  const activeFilters: { facet: FacetKey; value: string }[] = (
    ["theme", "format", "region"] as FacetKey[]
  ).flatMap((facet) =>
    [...selected[facet]].map((value) => ({ facet, value })),
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id="query"
          name="query"
          type="search"
          placeholder="Search datasets..."
          aria-label="Search datasets"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          className="min-w-40 flex-1"
        />

        <Select value={sort} onValueChange={(v) => onSortChange(v as CatalogueSort)}>
          <SelectTrigger className="w-44" aria-label="Sort by">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATALOGUE_SORTS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" data-testid="catalogue-filters">
              <SlidersHorizontal />
              Filters
              {activeCount > 0 && (
                <Badge className="ml-1 px-1.5">{activeCount}</Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-0">
            <div className="max-h-[60vh] overflow-y-auto p-3">
              <FacetGroup
                facet="theme"
                options={themeOptions}
                selected={selected.theme}
                onToggle={onToggle}
              />
              <FacetGroup
                facet="format"
                options={formatOptions}
                selected={selected.format}
                onToggle={onToggle}
                pending={facetsPending}
              />
              <FacetGroup
                facet="region"
                options={regionOptions}
                selected={selected.region}
                onToggle={onToggle}
                pending={facetsPending}
              />
            </div>
            {activeCount > 0 && (
              <div className="border-t p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={onClearAll}
                >
                  Clear all filters
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>

        <ToggleGroup
          type="single"
          variant="outline"
          value={view}
          onValueChange={(v) => v && onViewChange(v as CatalogueView)}
        >
          <ToggleGroupItem value="grid" aria-label="Grid view">
            <LayoutGrid />
          </ToggleGroupItem>
          <ToggleGroupItem value="list" aria-label="List view">
            <List />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {activeFilters.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          {activeFilters.map(({ facet, value }) => (
            <button
              key={`${facet}:${value}`}
              type="button"
              aria-label={`Remove ${value} filter`}
              onClick={() => onToggle(facet, value)}
              className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-muted-foreground hover:text-foreground"
            >
              {value}
              <X className="size-3" />
            </button>
          ))}
          <button
            type="button"
            onClick={onClearAll}
            className="ml-1 text-muted-foreground underline-offset-2 hover:underline"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

interface FacetGroupProps {
  facet: FacetKey;
  options: readonly string[];
  selected: Set<string>;
  onToggle: (facet: FacetKey, value: string) => void;
  pending?: boolean;
}

const FacetGroup = ({
  facet,
  options,
  selected,
  onToggle,
  pending,
}: FacetGroupProps) => (
  <div className="border-t py-3 first:border-t-0 first:pt-0">
    <h4 className="mb-2 text-sm font-medium">{FACET_LABELS[facet]}</h4>
    {pending && options.length === 0 ? (
      <p className="text-xs text-muted-foreground">Loading options...</p>
    ) : (
      <div className="space-y-1.5">
        {options.map((option) => {
          const id = `${facet}-${option}`.replace(/[^a-z0-9-]+/gi, "-");
          return (
            <div key={option} className="flex items-center gap-2">
              <Checkbox
                id={id}
                checked={selected.has(option)}
                onCheckedChange={() => onToggle(facet, option)}
              />
              <Label htmlFor={id} className="cursor-pointer font-normal">
                {option}
              </Label>
            </div>
          );
        })}
      </div>
    )}
  </div>
);
